/**
 * navDots — the small unread dot on Briefing and Alerts.
 *
 * One rule for both:
 *   something new arrives  → the dot appears
 *   first visit            → it stays (a glance is not a read)
 *   leave                  → it stays
 *   come back              → it goes, until the next new thing
 *
 * "Something new" is an event marker that only ever moves forward: today's
 * date for the briefing, the newest notification's time for Alerts. Deleting
 * a notification moves the marker backwards, which is deliberately not treated
 * as new — otherwise clearing one row would bring a dot back.
 *
 * Each device remembers, per account, the latest event it has seen and how
 * many visits it has had. That lives in localStorage and never leaves the
 * device; the cookie policy lists it under strictly necessary storage.
 */

import { useSyncExternalStore } from 'react';

const PREFIX = 'leeflet.dot.';
const listeners = new Set();
const keyFor = (uid, name) => `${PREFIX}${uid}.${name}`;

function read(uid, name) {
  try {
    const v = JSON.parse(localStorage.getItem(keyFor(uid, name)) || 'null');
    return v && typeof v === 'object' && v.event != null ? v : null;
  } catch {
    return null;
  }
}

function write(uid, name, value) {
  try { localStorage.setItem(keyFor(uid, name), JSON.stringify(value)); } catch { /* private mode */ }
  listeners.forEach((fn) => fn());
}

const valid = (event) => (typeof event === 'string' && event) || (typeof event === 'number' && event > 0);

/** Is this event already known (the same as, or older than, the one stored)? */
const known = (cur, event) => cur && typeof cur.event === typeof event && event <= cur.event;

/** One more visit to the page that owns this dot. */
export function recordVisit(uid, name, event) {
  if (!uid || !valid(event)) return;
  const cur = read(uid, name);
  if (known(cur, event)) {
    if ((cur.visits || 0) >= 2) return;
    write(uid, name, { event: cur.event, visits: (cur.visits || 0) + 1 });
  } else {
    write(uid, name, { event, visits: 1 });
  }
}

/** Whether the dot still shows: from the event until the second visit. */
export function dotVisible(uid, name, event) {
  if (!uid || !valid(event)) return false;
  const cur = read(uid, name);
  return !(known(cur, event) && (cur.visits || 0) >= 2);
}

function subscribe(fn) {
  listeners.add(fn);
  // Another tab visiting the page clears the dot here too.
  const onStorage = (e) => { if (!e.key || e.key.startsWith(PREFIX)) fn(); };
  window.addEventListener('storage', onStorage);
  return () => { listeners.delete(fn); window.removeEventListener('storage', onStorage); };
}

/** Live visibility of one dot. */
export function useDot(uid, name, event) {
  return useSyncExternalStore(subscribe, () => dotVisible(uid, name, event), () => false);
}

/** Forget every dot on this device — used when someone signs out. */
export function clearDots() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) localStorage.removeItem(k);
    }
  } catch { /* private mode */ }
  listeners.forEach((fn) => fn());
}
